import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '../environment.service';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client/core';
import {
  FacilitySignIn,
  FacilitySignInInput,
  PublicFacility,
} from '../../model/locations/facility-sign-in';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { FacilityBuilderService } from '../builder/facility-builder.service';
import { getPublicFacilities } from '../../graphql/facilities/get-public-facilites';
import { setContext } from '@apollo/client/link/context';
import { createFacilitySignIn } from '../../graphql/facilities/create-facility-sign-in';
import { getSignedInPeople } from 'src/app/graphql/facilities/get-signed-in-people';
import { createFacilitySignOutFromPhone } from 'src/app/graphql/facilities/create-facility-sign-out-from-phone';

@Injectable({
  providedIn: 'root',
})
export class PublicFacilityApiService {
  private _env = inject(EnvironmentService);
  private _facilityBuilder = inject(FacilityBuilderService);

  client!: ApolloClient<NormalizedCacheObject>;
  cache = new InMemoryCache();
  apiClient!: Observable<ApolloClient<NormalizedCacheObject>>;

  constructor() {
    this.configure();
  }

  async configure() {
    const url = this._env.getGraphQLURL();

    // API Key used for Public API rather than using the token.
    const authLink = setContext(async (_, { headers }) => {
      return {
        headers: {
          ...headers,
          'x-api-key': this._env.getpublicFunctionApiKey(),
        },
      };
    });

    const httpLink = new HttpLink({ uri: url });

    this.client = new ApolloClient({
      link: ApolloLink.from([authLink, httpLink]),
      cache: this.cache,
    });
  }

  getObservedClient(): Observable<ApolloClient<NormalizedCacheObject>> {
    return of(this.client);
  }

  getPublicFacilities(): Observable<PublicFacility[]> {
    return from(this.getObservedClient())
      .pipe(
        switchMap((client) => {
          return client.query({
            query: getPublicFacilities,
            variables: {},
            fetchPolicy: 'no-cache',
          });
        })
      )
      .pipe(
        map((data: any) => {
          if (!data.data['getPublicFacilities']) {
            console.warn(
              `Got ${data.data['getPublicFacilities']} when attempting to retrieve interior graphql response for query name: ${'getPublicFacilities'}, double check capitalization and spelling, full response follows`,
              data
            );
          }

          return data.data['getPublicFacilities'].map((i: any) =>
            this._facilityBuilder.buildPublicFacility(i)
          );
        })
      );
  }

  createFacilitySignIn(
    id: string | null,
    input: FacilitySignInInput | null
  ): Observable<FacilitySignIn> {
    return from(this.getObservedClient())
      .pipe(
        switchMap((client) => {
          return client.mutate({
            mutation: createFacilitySignIn,
            variables: { id, input },
          });
        })
      )
      .pipe(
        map((data: any) => {
          if (!data.data['createFacilitySignIn']) {
            console.warn(
              `Got ${data.data['createFacilitySignIn']} when attempting to retrieve interior graphql response for mutation name: ${'createFacilitySignIn'}, double check capitalization and spelling, full response follows`,
              data
            );
          }
          return this._facilityBuilder.buildFacilitySignIn(
            data.data['createFacilitySignIn']
          );
        })
      );
  }

  createFacilitySignOutFromPhone(
    facilityId: string,
    phoneNumber: string
  ): Observable<any> {
    return from(this.getObservedClient()).pipe(
      switchMap((client) => {
        return client.mutate({
          mutation: createFacilitySignOutFromPhone,
          variables: { facilityId, phoneNumber },
        });
      })
    );
  }
}
